/* Campaign-specific dismissal expiry and coupon recovery. No network requests. */
(() => {
  'use strict';
  const root = document.querySelector('[data-swe-coupon-access]');
  if (!root || window.Shopify?.designMode || new URLSearchParams(location.search).get('popup-preview') === 'true') return;
  const id = Number(root.dataset.popupId);
  const day = 24 * 60 * 60 * 1000;
  const readyAt = Date.now() + 15000;
  const stateKey = `swe:coupon:${id}:v1`;
  const widgetKey = `ecomsend_close_widget_modal${id}`;
  const popupKeys = [`ecomsend_pp_window_normal${id}`, `ecomsend_pp_already_show${id}`];
  const dialog = root.querySelector('dialog');
  const reminder = root.querySelector('[data-swe-coupon-reminder]');
  let state = { popupUntil: 0, widgetUntil: 0 };
  let actions, wakeTimer, scrollTimer, opener, pendingTrigger;
  let watchingScroll = false;

  function get(storage, key) {
    try { return window[storage].getItem(key); } catch { return null; }
  }
  function setSession(key, value) {
    try {
      if (value === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, value);
    } catch { /* Keep the native app usable when storage is unavailable. */ }
  }
  function parseState(raw) {
    try {
      const value = JSON.parse(raw);
      const time = key => Number.isFinite(value?.[key]) && value[key] > 0 ? value[key] : 0;
      return { popupUntil: time('popupUntil'), widgetUntil: time('widgetUntil') };
    } catch { return { popupUntil: 0, widgetUntil: 0 }; }
  }
  function readState() {
    const raw = get('localStorage', stateKey);
    return raw === null ? state : parseState(raw);
  }
  function save(patch) {
    state = { ...readState(), ...patch };
    try { window.localStorage.setItem(stateKey, JSON.stringify(state)); } catch { /* In-memory fallback. */ }
  }
  function cookie(name) {
    const value = document.cookie.split(';').map(part => part.trim()).find(part => part.startsWith(`${name}=`));
    try { return value ? decodeURIComponent(value.slice(name.length + 1)) : ''; } catch { return ''; }
  }
  function claimed() {
    return root.dataset.claimed === 'true' || cookie(`ecomsend_pp_success_subscribed${id}`) === 'true' || Boolean(cookie(`ecomsend_discount_code${id}`));
  }
  function config() {
    return actions?.getTargetPopupConfig(id) || actions?.hashTriggerPopupPool?.find(popup => Number(popup.id) === id);
  }
  function nativeId() { return config()?.id ?? id; }
  function flushOpen() {
    if (!pendingTrigger || !actions || !config()) return;
    pendingTrigger.removeAttribute('aria-busy');
    pendingTrigger = null;
    actions.showModal(true, false, nativeId());
  }
  function syncSession() {
    state = readState();
    const now = Date.now();
    setSession(widgetKey, state.widgetUntil > now ? 'true' : null);
    for (const key of popupKeys) setSession(key, state.popupUntil > now ? 'true' : null);
  }
  function stopScroll() {
    document.removeEventListener('scroll', onScroll);
    window.clearTimeout(scrollTimer);
    scrollTimer = 0;
    watchingScroll = false;
  }
  function watchScroll() {
    if (watchingScroll) return;
    document.addEventListener('scroll', onScroll, { passive: true });
    watchingScroll = true;
  }
  function onScroll() {
    if (scrollTimer) return;
    scrollTimer = window.setTimeout(() => {
      scrollTimer = 0;
      attemptAuto();
    }, 200);
  }
  function excludedPage() {
    return /\/(cart|checkout|checkouts|account|accounts)(\/|$)/.test(location.pathname);
  }
  function busy() {
    if (document.body.classList.contains('js-drawer-open')) return true;
    if (document.activeElement?.closest('input, textarea, select, [contenteditable="true"]')) return true;
    return Array.from(document.querySelectorAll('dialog[open], [aria-modal="true"]')).some(element => !element.closest('[aria-hidden="true"], [hidden]') && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
  }
  function scheduleWake() {
    window.clearTimeout(wakeTimer);
    const now = Date.now();
    const dates = [state.widgetUntil];
    if (!claimed() && !excludedPage()) dates.push(state.popupUntil, readyAt);
    const next = Math.min(...dates.filter(time => time > now));
    if (Number.isFinite(next)) wakeTimer = window.setTimeout(resume, Math.min(next - now + 25, day));
  }
  function attemptAuto() {
    if (actions?.getTargetPopupConfig(id)) actions.showModal(false, false, nativeId());
  }
  function renderReminder() { reminder.hidden = !config()?.status || state.widgetUntil > Date.now(); }
  function resume() {
    syncSession();
    if (document.hidden) return;
    renderReminder();
    attemptAuto();
    scheduleWake();
  }
  function recover() {
    const current = config();
    const code = current?.rules?.discount_coupon?.manually_code || cookie(`ecomsend_discount_code${id}`);
    if (!current?.status || !code || typeof dialog.showModal !== 'function') return false;
    root.querySelector('[data-swe-coupon-code]').textContent = code;
    root.querySelector('[data-swe-coupon-apply]').href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(location.pathname + location.search)}`;
    if (!dialog.open) {
      opener = document.activeElement;
      dialog.showModal();
    }
    return true;
  }
  function install() {
    if (actions) return;
    const native = window.EcomSendApps?.actions;
    if (!native || !['showModal', 'sendModalReport', 'setHashTriggerPopupPool', 'getTargetPopupConfig'].every(key => typeof native[key] === 'function')) return;
    actions = native;
    document.removeEventListener('load', onLoad, true);
    state = readState();
    const now = Date.now();
    // Give old, indefinite session dismissals an expiry during the rollout.
    const nativeExpiry = Number(get('localStorage', `ecomsend_expires_time${id}`)) * 1000;
    if (!state.popupUntil && (popupKeys.some(key => get('sessionStorage', key) === 'true') || nativeExpiry > now && cookie(`ecomsend_modal_limit${id}`))) {
      save({ popupUntil: nativeExpiry || now + day });
    }
    if (!state.widgetUntil && get('sessionStorage', widgetKey) === 'true') save({ widgetUntil: now + day });
    syncSession();

    const originalShow = native.showModal;
    native.showModal = function(manual, preview, popupId, scrollPassed) {
      if (Number(popupId) !== id || preview) return originalShow.apply(this, arguments);
      if (manual) {
        if (claimed() && recover()) return;
        return originalShow.call(this, manual, preview, nativeId(), true);
      }
      syncSession();
      if (claimed() || excludedPage() || state.popupUntil > Date.now()) {
        stopScroll();
        scheduleWake();
        return;
      }
      if (document.hidden || !config()?.status) return;
      if (Date.now() < readyAt) { scheduleWake(); return; }
      watchScroll();
      const height = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight) - window.innerHeight;
      const progress = height > 0 ? window.scrollY / height * 100 : 0;
      if (busy() || progress < Number(config().rules.trigger.when.scroll_offset || 10)) return;
      return originalShow.call(this, false, false, nativeId(), true);
    };
    const originalReport = native.sendModalReport;
    native.sendModalReport = function(popupId) {
      if (Number(popupId) === id) {
        save({ popupUntil: Date.now() + day });
        stopScroll();
        scheduleWake();
      }
      return originalReport.apply(this, arguments);
    };
    const originalPool = native.setHashTriggerPopupPool;
    native.setHashTriggerPopupPool = function() {
      const result = originalPool.apply(this, arguments);
      renderReminder();
      queueMicrotask(flushOpen);
      return result;
    };
    renderReminder();
    queueMicrotask(flushOpen);
    scheduleWake();
  }
  function onLoad(event) {
    if (event.target.tagName === 'SCRIPT') install();
  }
  document.addEventListener('load', onLoad, true);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-swe-coupon-open]');
    if (!trigger) return;
    event.preventDefault();
    // An explicit request restores access, including a reminder they dismissed.
    if (readState().widgetUntil > Date.now()) {
      save({ widgetUntil: 0 });
      syncSession();
      renderReminder();
    }
    pendingTrigger = trigger;
    trigger.setAttribute('aria-busy', 'true');
    flushOpen();
  }, true);
  document.addEventListener('focusout', () => { if (watchingScroll) onScroll(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) resume(); });
  window.addEventListener('pageshow', resume);
  window.addEventListener('storage', event => {
    if (event.key !== stateKey) return;
    state = parseState(event.newValue);
    syncSession();
    if (state.popupUntil > Date.now()) stopScroll();
    resume();
  });
  root.querySelector('[data-swe-reminder-close]').addEventListener('click', () => {
    const until = Date.now() + day;
    save({ widgetUntil: until, popupUntil: Math.max(readState().popupUntil, until) });
    syncSession();
    reminder.hidden = true;
    stopScroll();
    scheduleWake();
  });
  root.querySelector('[data-swe-coupon-close]').addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
  dialog.addEventListener('close', () => opener?.focus({ preventScroll: true }));
  install();
})();
